/* eslint-disable no-return-await */
import { AppsService, getAppIcon } from '@/common/apps/apps.service';
import { generateDesignerLastUrlUserSettingsKey } from '@/guides/helpers/common.util.js';
import { APP_PLATFORM_TYPES, getAppDisplayName, getAppsForPlatforms, getIntegratedApps, isWebBasedApp } from '@/utils/apps';
import { getAutoFeatureConfig, updateAutoFeatureConfigProperty } from '@/utils/auto-feature';
import { getDisplayName } from '@pendo/services/Apps';
import { SdkTools } from '@pendo/services/BuildingBlocks';
import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import values from 'lodash/values';
import Vue from 'vue';
import {
    addKnowledgeStore,
    deactivateApp,
    fetchApp,
    fetchApps,
    fetchDiscoveryApps,
    getAllKnowledgeStores,
    getKnowledgeStores,
    getMobileAppKey,
    updateApp,
    updateAppFlag,
    updateAppProperty,
    updateDiscoveryAppCost,
    updateDiscoveryAppDescription,
    updateDiscoveryAppLabels,
    updateDiscoveryAppLicenses,
    updateDiscoveryAppOwner,
    updateDiscoveryAppRenewalDate,
    updateDomains,
} from './apps.utils';

const { isMobileCodelessApp } = SdkTools;

const DEFAULT_TIMEOUT = 10000;

export function getInitialState () {
    return {
        map: {},
        discoveryApps: [],
        error: null,
        updatingFlag: null,
        updatingApp: false,
        autoFeatureConfig: null,
        knowledgeStore: [],
        allKnowledgeStores: [],
        deactivatedAppIdsLoaded: false,
    };
}

const apps = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setApp (state, { app }) {
            Vue.set(state.map, app.id, app);
        },
        setMobileAppKey (state, { appId, mobileAppKey }) {
            Vue.set(state.map[appId], 'mobileAppKey', mobileAppKey);
        },
        setKnowledgeStores (state, { knowledgeStores }) {
            state.knowledgeStores = knowledgeStores;
        },
        setAllKnowledgeStores (state, { allKnowledgeStores }) {
            state.allKnowledgeStores = allKnowledgeStores;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setUpdatingFlag (state, { flag }) {
            state.updatingFlag = flag;
        },
        setFlag (state, { appId, flag, enabled }) {
            Vue.set(state.map[appId].applicationFlags, flag, enabled);
        },
        setAppProperty (state, { id, propertyName, propertyValue }) {
            Vue.set(state.map[id], propertyName, propertyValue);
        },
        setUpdatingApp (state, { updating }) {
            state.updatingApp = updating;
        },
        setAgentProductionVersion (state, { appId, version }) {
            const appConfig = state.map[appId];
            if (appConfig && appConfig.agentVersionProd) {
                appConfig.agentVersionProd = version;
            }
        },
        setAutoFeatureConfig (state, { appId, autoFeatureConfig }) {
            state.map[appId].autoFeatureConfig = autoFeatureConfig;
        },
        setDiscoveryApps (state, { discoveryApps }) {
            state.discoveryApps = discoveryApps;
        },
        setDiscoveryAppLabels (state, { appId, labels }) {
            const index = state.discoveryApps.findIndex((a) => a.id === appId);
            if (index !== -1) {
                Vue.set(state.discoveryApps[index], 'labels', labels);
            }
        },
        setDiscoveryAppRenewalDate (state, { appId, renewalDate }) {
            const index = state.discoveryApps.findIndex((a) => a.id === appId);
            if (index !== -1) {
                Vue.set(state.discoveryApps[index], 'renewalDate', renewalDate);
            }
        },
        setDeactivatedAppIdsLoaded (state, loaded) {
            state.deactivatedAppIdsLoaded = loaded;
        },
    },
    actions: {
        async fetchDiscoveryApps ({ commit }) {
            try {
                const discoveryApps = await fetchDiscoveryApps();
                commit('setDiscoveryApps', { discoveryApps });

                return discoveryApps;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateDiscoveryAppLicenses (_context, { discoveryAppId, licenses }) {
            return await updateDiscoveryAppLicenses(discoveryAppId, licenses);
        },
        async updateDiscoveryAppCost (_context, { appId, cost }) {
            return await updateDiscoveryAppCost(appId, cost);
        },
        async updateDiscoveryAppOwner (_context, { appId, ownedByUserId }) {
            return await updateDiscoveryAppOwner(appId, ownedByUserId);
        },
        async updateDiscoveryAppRenewalDate ({ commit }, { appId, renewalDate }) {
            const result = await updateDiscoveryAppRenewalDate(appId, renewalDate);
            commit('setDiscoveryAppRenewalDate', { appId, renewalDate });

            return result;
        },
        async hydrate ({ dispatch, commit }, { applications } = {}) {
            if (!applications) {
                await dispatch('loadAll', { reFetch: true });

                return;
            }
            commit('setMap', { map: keyBy(applications, 'id') });
        },
        async loadAll ({ commit, state, rootGetters }, { reFetch = false, includeDeactivated } = {}) {
            const needsDeactivated = includeDeactivated && !state.deactivatedAppIdsLoaded;
            if (!isEmpty(state.map) && !reFetch && !needsDeactivated) {
                return;
            }

            try {
                const applications = await fetchApps({
                    includeDeactivated: includeDeactivated || state.deactivatedAppIdsLoaded,
                });

                // We only need to check for training attributes overrides in AEU subscriptions
                const isAdoptEndUser = rootGetters['subscription/isAdoptEndUser'];
                if (isAdoptEndUser) {
                    applications.forEach((app) => {
                        app.displayName = getAppDisplayName(app);
                    });
                }

                commit('setMap', { map: keyBy(applications, 'id') });
                if (includeDeactivated) {
                    commit('setDeactivatedAppIdsLoaded', true);
                }
            } catch (error) {
                commit('setError', { error });
            }
        },
        async loadOne ({ commit, state, rootGetters }, { reFetch = false, id } = {}) {
            if (state.map[id] && !reFetch) {
                return;
            }

            try {
                const app = await fetchApp(id);

                // We only need to check for training attributes overrides in AEU subscriptions
                const isAdoptEndUser = rootGetters['subscription/isAdoptEndUser'];
                if (isAdoptEndUser) {
                    app.displayName = getAppDisplayName(app);
                }

                commit('setApp', { app });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async deactivateApp ({ commit }, { id }) {
            try {
                await deactivateApp(id);
                commit('setFlag', { appId: id, flag: 'disabled', enabled: true });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateApp ({ commit, state, dispatch, rootGetters }, { app, updates }) {
            const existingApp = state.map[app.id];
            try {
                commit('setUpdatingApp', { updating: true });

                const updatedApp = await updateApp({
                    ...existingApp,
                    ...updates,
                });

                // We only need to check for training attributes overrides in AEU subscriptions
                const isAdoptEndUser = rootGetters['subscription/isAdoptEndUser'];
                if (isAdoptEndUser) {
                    updatedApp.displayName = getAppDisplayName(updatedApp);
                }

                commit('setApp', { app: updatedApp });

                // PUT to app endpoint does not return mobileAppKey so it needs to be fetched again
                if (AppsService.isMobile(updatedApp)) {
                    await dispatch('getMobileAppKey', { appId: updatedApp.id });
                }

                commit('setUpdatingApp', { updating: false });
            } catch (error) {
                commit('setUpdatingApp', { updating: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateDomains (storeInjections, { app, urls }) {
            await updateDomains(app.id, urls);
        },
        async updateFlag ({ commit }, { appId, flag, enabled }) {
            commit('setUpdatingFlag', { flag });
            try {
                await updateAppFlag(appId, flag, enabled);
                commit('setFlag', { appId, flag, enabled });
                commit('setUpdatingFlag', { flag: null });
            } catch (error) {
                commit('setError', { error });
                commit('setUpdatingFlag', { flag: null });
                throw error;
            }
        },
        async getMobileAppKey ({ commit }, { appId }) {
            try {
                commit('setError', { error: null });
                const { mobileAppKey } = await getMobileAppKey(appId);
                commit('setMobileAppKey', { appId, mobileAppKey });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async getKnowledgeStores ({ commit }, { orgId, appId }) {
            if (!orgId) return;
            try {
                commit('setError', { error: null });
                const knowledgeStores = await getKnowledgeStores(orgId, appId);
                commit('setKnowledgeStores', { knowledgeStores });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async getAllKnowledgeStores ({ commit }, { orgId }) {
            if (!orgId) return;
            try {
                commit('setError', { error: null });
                const allKnowledgeStores = await getAllKnowledgeStores(orgId);
                commit('setAllKnowledgeStores', { allKnowledgeStores });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async addKnowledgeStore ({ commit }, { orgId, url, appId, subId }) {
            try {
                commit('setError', { error: null });
                const knowledgeStores = await addKnowledgeStore(orgId, url, appId, subId);
                commit('setKnowledgeStores', { knowledgeStores });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async updateAppProperty ({ commit }, { propertyName, propertyValue, id }) {
            try {
                commit('setError', { error: null });
                const updatedAppProperty = await updateAppProperty(id, propertyName, propertyValue);
                commit('setAppProperty', { id, propertyName, propertyValue: updatedAppProperty });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateAttributesOnMultipleApps ({ commit, dispatch }, { apps, updates }) {
            try {
                commit('setUpdatingApp', { updating: true });
                const updateAppRequests = apps.map((app) => {
                    return dispatch('updateApp', {
                        app,
                        updates,
                    });
                });
                await Promise.all(updateAppRequests);
                commit('setUpdatingApp', { updating: false });
            } catch (error) {
                commit('setUpdatingApp', { updating: false });
                throw error;
            }
        },
        async fetchAutoFeatureConfig ({ commit }, appId) {
            const autoFeatureConfig = await getAutoFeatureConfig(appId);
            commit('setAutoFeatureConfig', { appId, autoFeatureConfig });
        },
        async updateAutoFeatureConfig ({ commit, state }, { appId, property, value }) {
            // this is a hack for a backend bug where an empty array is returned as "null" instead of "[]"
            // as a result, if we're testing the "oldValue" in the PATCH, we need to convert the value to
            // null if it's an empty array.
            const testValue = state.map[appId]?.autoFeatureConfig[property];
            const autoFeatureConfig = await updateAutoFeatureConfigProperty(
                appId,
                property,
                testValue?.length === 0 ? null : testValue,
                value,
            );
            commit('setAutoFeatureConfig', { appId, autoFeatureConfig });
        },
        async updateDiscoveryAppDescription (_context, { appId, description }) {
            return await updateDiscoveryAppDescription(appId, description);
        },
        async updateDiscoveryAppLabels ({ commit }, { appId, labels }) {
            const result = await updateDiscoveryAppLabels(appId, labels);
            commit('setDiscoveryAppLabels', { appId, labels });

            return result;
        },
    },
    getters: {
        getAppById: (state) => (id) => state.map[id],
        getAppsByIds: (state) => (ids) => ids.map((id) => state.map[id]).filter(Boolean),
        getAppOrCategoryById: (state) => (id) => {
            if (id === '*' || id === 'all') return { id: 'all', displayName: 'All Apps' };
            if (id === 'web') return { id: 'web', displayName: 'Web Apps' };
            if (id === 'mobile') return { id: 'mobile', displayName: 'Mobile Apps' };
            if (id === 'extension') return { id: 'extension', displayName: 'Extension Apps' };
            if (id === 'web|extension') return { id: 'web|extension', displayName: 'Web & Extension Apps' };

            return state.map[id];
        },
        list: (state) => values(state.map).filter((app) => !AppsService.isDeactivatedApp(app)),
        discoveryAppsList: (state) => state.discoveryApps,
        deactivatedList: (state) => values(state.map).filter((app) => AppsService.isDeactivatedApp(app)),
        listAllForActiveSubscription: (state, getters, rootState) =>
            getters.list.filter((app) => app.subscriptionId === rootState.subscription.active.id),
        appMapForActiveSubscription: (state, getters) => keyBy(getters.listAllForActiveSubscription, 'id'),
        allWebPlusIntegratedMobileApps: (state, getters) =>
            getters.list.filter((app) => app.platform === APP_PLATFORM_TYPES.WEB || app.integrated),
        onlyIntegratedApps: (state, getters) => getters.list.filter((app) => app.integrated),
        webList: (state, getters) => getAppsForPlatforms(getters.list, [APP_PLATFORM_TYPES.WEB]),
        extensionList: (state, getters) => getAppsForPlatforms(getters.list, [APP_PLATFORM_TYPES.EXTENSION]),
        webLikeList: (state, getters) =>
            getAppsForPlatforms(getters.list, [APP_PLATFORM_TYPES.EXTENSION, APP_PLATFORM_TYPES.WEB]),
        mobileList: (state, getters) =>
            getAppsForPlatforms(getters.list, [APP_PLATFORM_TYPES.IOS, APP_PLATFORM_TYPES.ANDROID]),
        integratedWebList: (state, getters) => getIntegratedApps(getters.webList),
        integratedExtensionList: (state, getters) => getIntegratedApps(getters.extensionList),
        integratedMobileList: (state, getters) => getIntegratedApps(getters.mobileList),
        hasIntegratedApps: (state, getters) => {
            return getters.list.some((app) => app.integrated === true);
        },
        retroAnalyticsList: (state, getters) => {
            const list = getters.allWebPlusIntegratedMobileApps;

            return list.filter((app) => {
                return isWebBasedApp(app.platform) || get(app, 'applicationFlags.mobileRetroactiveAnalyticsApp', false);
            });
        },
        hasMobileApp: (state, getters) => {
            const apps = getters.list;
            if (apps.length && apps.length > 0) {
                return getAppsForPlatforms(apps, [APP_PLATFORM_TYPES.IOS, APP_PLATFORM_TYPES.ANDROID]);
            }

            return false;
        },
        hasV2MobileApp: (state, getters) => {
            const apps = getters.allWebPlusIntegratedMobileApps;

            return apps.some((app) => AppsService.isV2MobileApp(app));
        },
        isMultiApp: (state, getters) => {
            const apps = getters.allWebPlusIntegratedMobileApps;

            return !!apps.length && apps.length > 1;
        },
        appsFromGuide: (state, getters) => (guide) => {
            const appMap = getters.appMapForActiveSubscription;

            return guide.appIds.map((id) => get(appMap, id, {}));
        },
        appNameFromId: (state, getters) => (id) => {
            const appMap = getters.appMapForActiveSubscription;
            const app = get(appMap, id, {});

            return getDisplayName(app);
        },
        getApplicationFlag: (state, getters) => (appId, flag) => {
            const app = getters.getAppById(appId);

            return get(app, `applicationFlags.${flag}`, false);
        },
        isRestrictedMobileApp: (state, getters) => (appId) => {
            const app = getters.getAppById(appId);

            return AppsService.isMobile(app) ? !isMobileCodelessApp(app) : false;
        },
        getMobileFrameworkFlavour: (state, getters) => (appId) => getters.getAppById(appId).mobileFrameworkFlavour,
        isAppLocalizationEnabled: (state, getters) => (appId) => {
            const app = getters.getAppById(appId);

            return get(app, 'applicationFlags.guideLocalization', false);
        },
        isAppPlatformType: (state, getters) => (appId, platform) => {
            const app = getters.getAppById(appId);

            return app?.platform === platform;
        },
        hasAppOfPlatformType: (state, getters) => (extensionPlatformType) => {
            if (extensionPlatformType === 'other') {
                return false;
            }

            const apps = getters.extensionList;

            return some(apps, { extensionPlatformType });
        },
        appsWithSuggestedPages: (state, getters) => {
            const applications = getters.list;

            return applications.filter((app) => app.applicationFlags.suggestPageTags);
        },
        appsWithPageBasedEventProperties: (state, getters) => {
            const applications = getters.list;

            return applications.filter((app) => get(app, 'applicationFlags.pageBasedEventProperties', false));
        },
        guideSeenTimeoutLength: (state, getters) => {
            // Assumes all web and extension apps have same agent flag value for now
            const app = first(getters.webLikeList);

            return get(app, 'guideSeenTimeoutLength', DEFAULT_TIMEOUT);
        },
        getAutoFeatureConfig: (state) => (appId) => {
            return state.map[appId]?.autoFeatureConfig;
        },
        locationKey: (state, getters) => (appId) => {
            const { subscriptionId } = getters.getAppById(appId);

            return generateDesignerLastUrlUserSettingsKey(subscriptionId, appId);
        },
        adoptLocationKey: (state, getters) => (appId) => {
            const { subscriptionId } = getters.getAppById(appId);

            return `${subscriptionId}:${appId}--lastDesignerUrl`;
        },
        lastKnownAppUrls: (state, getters, rootState, rootGetters) => {
            return getters.list.reduce((urlMap, app) => {
                const { id } = app;

                urlMap[id] =
                    rootGetters['userSettings/settingByName'](getters.locationKey(id))?.value ||
                    rootGetters['userSettings/settingByName'](getters.adoptLocationKey(id))?.value;

                return urlMap;
            }, {});
        },
        getEndUserMessaging: (state, getters) => (appId) => {
            return getters.getApplicationFlag(appId, 'adoptAnalyticsForwarding')
                ? 'Reseller Partners'
                : 'Adopt end users';
        },
        analyticsAppsListWithIcons: (state, getters) => {
            if (Object.keys(state.map).length === 0) return null;

            return AppsService.sortApps(
                getters.list
                    .filter((el) => el.integrated && !el.isDeleted)
                    .map((app) =>
                        Object.freeze({
                            id: app.id,
                            kind: 'anyActivity',
                            platform: app.platform,
                            isDeleted: !!app.isDeleted,
                            integrated: !!app.integrated,
                            firstEventTime: app.firstEventTime,
                            iconUrl: app.iconUrl,
                            icon: getAppIcon(app),
                            codeName: app.name,
                            name: app.displayName,
                            displayName: app.displayName,
                            applicationFlags: app.applicationFlags,
                            ...(app.recordingStartingEventTime && {
                                recordingStartingEventTime: app.recordingStartingEventTime,
                            }),
                            ...(app.trainingAttributes && { trainingAttributes: app.trainingAttributes }),
                        }),
                    ),
            );
        },
        getIconByAppId: (state, getters) => (appId) => {
            const app = getters.getAppById(appId);

            return getAppIcon(app);
        },
        getAppsWithoutVisitorType: (_state, getters) => getters.list.filter((app) => !app.visitorType),
        firstAvailableApp: (state, getters) => {
            const allApps = getters.analyticsAppsListWithIcons;
            if (!allApps || allApps.length === 0) {
                return null;
            }

            const firstItem = allApps[0];
            if (firstItem?.apps && firstItem.apps.length > 0) {
                return firstItem.apps[0];
            }

            return firstItem;
        },
    },
};

export default apps;
